/**
 * Custom Error Classes
 */

import type { ErrorContext, ErrorOptions, ValidationErrorField } from './types'

/**
 * Base error class for all application errors
 */
export class AppError extends Error {
  public readonly code: string
  public readonly statusCode: number
  public readonly isOperational: boolean
  public readonly originalError?: unknown
  public readonly context: Partial<ErrorContext>
  public readonly timestamp: string

  constructor(
    message: string,
    options: ErrorOptions = {}
  ) {
    super(message)
    this.name = this.constructor.name
    this.code = options.code || 'UNKNOWN_ERROR'
    this.statusCode = options.statusCode || 500
    this.isOperational = options.isOperational ?? true
    this.originalError = options.originalError
    this.timestamp = new Date().toISOString()
    this.context = {
      ...options.context,
      code: this.code,
      statusCode: this.statusCode,
      isOperational: this.isOperational,
      timestamp: this.timestamp
    }

    // `Error.captureStackTrace` is a V8/Node-only API; Safari (both iOS
    // and macOS) doesn't ship it. Calling it unconditionally threw a
    // TypeError at every AppError construction on Safari, which cascaded
    // into unhandled promise rejections on surfaces like
    // `/therapist/[slug]/book/confirm` and silently stalled the booking
    // UI. Feature-detect and skip on engines without it — the stack
    // trace was decorative anyway (we persist errors via the server
    // observability pipeline, not from client-side AppError stacks).
    if (typeof (Error as unknown as { captureStackTrace?: unknown }).captureStackTrace === 'function') {
      Error.captureStackTrace(this, this.constructor)
    }
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      timestamp: this.timestamp,
      context: this.context
    }
  }
}

/**
 * Authentication related errors
 */
export class AuthError extends AppError {
  constructor(message: string = 'Authentication failed', options: ErrorOptions = {}) {
    super(message, {
      code: 'AUTH_ERROR',
      statusCode: 401,
      ...options
    })
  }
}

/**
 * Authorization/Permission errors
 */
export class PermissionError extends AppError {
  constructor(message: string = 'Permission denied', options: ErrorOptions = {}) {
    super(message, {
      code: 'PERMISSION_DENIED',
      statusCode: 403,
      ...options
    })
  }
}

/**
 * Validation errors
 */
export class ValidationError extends AppError {
  public readonly fields: ValidationErrorField[]

  constructor(
    message: string = 'Validation failed',
    fields: ValidationErrorField[] = [],
    options: ErrorOptions = {}
  ) {
    super(message, {
      code: 'VALIDATION_ERROR',
      statusCode: 400,
      ...options
    })
    this.fields = fields
  }

  addField(field: ValidationErrorField) {
    this.fields.push(field)
  }

  toJSON() {
    return {
      ...super.toJSON(),
      fields: this.fields
    }
  }
}

/**
 * Not found errors
 */
export class NotFoundError extends AppError {
  constructor(
    resource: string = 'Resource',
    options: ErrorOptions = {}
  ) {
    super(`${resource} not found`, {
      code: 'NOT_FOUND',
      statusCode: 404,
      ...options
    })
  }
}

/**
 * Conflict errors (e.g., duplicate resources)
 */
export class ConflictError extends AppError {
  constructor(message: string = 'Resource conflict', options: ErrorOptions = {}) {
    super(message, {
      code: 'CONFLICT',
      statusCode: 409,
      ...options
    })
  }
}

/**
 * Network errors
 */
export class NetworkError extends AppError {
  constructor(message: string = 'Network error occurred', options: ErrorOptions = {}) {
    super(message, {
      code: 'NETWORK_ERROR',
      statusCode: 503,
      ...options
    })
  }
}

/**
 * Rate limit errors
 */
export class RateLimitError extends AppError {
  public readonly retryAfter?: number

  constructor(
    message: string = 'Rate limit exceeded',
    retryAfter?: number,
    options: ErrorOptions = {}
  ) {
    super(message, {
      code: 'RATE_LIMIT_ERROR',
      statusCode: 429,
      ...options
    })
    this.retryAfter = retryAfter
  }

  toJSON() {
    return {
      ...super.toJSON(),
      retryAfter: this.retryAfter
    }
  }
}

/**
 * Database errors
 */
export class DatabaseError extends AppError {
  constructor(message: string = 'Database error occurred', options: ErrorOptions = {}) {
    super(message, {
      code: 'DATABASE_ERROR',
      statusCode: 500,
      isOperational: false,
      ...options
    })
  }
}

/**
 * External API errors
 */
export class ApiError extends AppError {
  public readonly apiName?: string
  public readonly endpoint?: string

  constructor(
    message: string = 'External API error',
    apiName?: string,
    endpoint?: string,
    options: ErrorOptions = {}
  ) {
    super(message, {
      code: 'API_ERROR',
      statusCode: 502,
      ...options
    })
    this.apiName = apiName
    this.endpoint = endpoint
  }

  toJSON() {
    return {
      ...super.toJSON(),
      apiName: this.apiName,
      endpoint: this.endpoint
    }
  }
}

/**
 * Server errors
 */
export class ServerError extends AppError {
  constructor(message: string = 'Internal server error', options: ErrorOptions = {}) {
    super(message, {
      code: 'SERVER_ERROR',
      statusCode: 500,
      isOperational: false,
      ...options
    })
  }
}